import { useCreateThread, useSelf, useThreads } from '@liveblocks/react'
import { useEffect } from 'react'

import { type EventProperties, useTrackCallback } from '@/lib/analytics/events'
import { sortByTimestamp } from '@/lib/date/date'
import { scrollIntoViewWhenStops } from '@/lib/ui/animation'
import { UrlParam, useUrlParams } from '@/lib/url/url-param.hook'

import { commentAnchorId } from '@/components/shared/comment/consts'
import { useCommentUsersReady } from '@/components/shared/comment/use-comment-users-ready'
import { useSendCommentNotification } from '@/components/shared/comment/use-send-comment-notification'
import { mentionEventProps } from '@/components/shared/comment/utils'

import { AssessmentCommentComposer } from './assessment-comment-composer'
import { AssessmentCommentsFeed } from './assessment-comments-feed'
import { ASSESSMENT_COMMENTS_LABELS } from './labels'
import type { CommentFilter } from './types'
import { useTrackCommentSearch } from './use-track-comment-search'
import { threadMatchesFilter, threadMatchesSearch } from './utils'

type AssessmentCommentsBodyProps = {
  assessmentId: string
  search: string | null
  filter: CommentFilter
  readOnly: boolean
  trackingProps: EventProperties
}

export const AssessmentCommentsBody = ({
  assessmentId,
  search,
  filter,
  readOnly,
  trackingProps,
}: AssessmentCommentsBodyProps) => {
  const self = useSelf()
  const createThread = useCreateThread()
  const { threads, isLoading, error } = useThreads({
    query: { metadata: { assessmentId, audience: 'internal' } },
  })
  const areAuthorsReady = useCommentUsersReady()
  const sendNotification = useSendCommentNotification()
  const trackCommentAdded = useTrackCallback('assessment.comment.added')
  const { urlParams } = useUrlParams()
  const highlightedCommentId = urlParams[UrlParam.COMMENT_ID]

  useEffect(() => {
    if (!highlightedCommentId || isLoading) return
    scrollIntoViewWhenStops(() => document.getElementById(commentAnchorId(highlightedCommentId)))
  }, [highlightedCommentId, isLoading])

  const allThreads = sortByTimestamp(threads ?? [], (thread) => thread.createdAt)
  const searchQuery = search?.trim() ?? ''
  const visibleThreads = allThreads.filter(
    (thread) =>
      threadMatchesSearch(thread.comments, searchQuery) &&
      threadMatchesFilter(thread.comments, filter, self?.id),
  )

  useTrackCommentSearch({
    query: searchQuery,
    resultCount: visibleThreads.length,
    threadCount: allThreads.length,
    trackingProps,
  })

  return (
    <>
      <div className='flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto'>
        <AssessmentCommentsFeed
          assessmentId={assessmentId}
          isLoading={isLoading || !areAuthorsReady}
          hasError={!!error}
          threadCount={allThreads.length}
          visibleThreads={visibleThreads}
          search={searchQuery}
          readOnly={readOnly}
          highlightedCommentId={highlightedCommentId}
          trackingProps={trackingProps}
        />
      </div>

      {!readOnly && search === null && (
        <AssessmentCommentComposer
          placeholder={
            allThreads.length === 0
              ? ASSESSMENT_COMMENTS_LABELS.firstCommentPlaceholder
              : ASSESSMENT_COMMENTS_LABELS.composerPlaceholder
          }
          onSubmit={(comment) => {
            const thread = createThread({
              body: comment.body,
              metadata: { assessmentId, audience: 'internal' },
            })
            trackCommentAdded({ ...trackingProps, ...mentionEventProps(comment.body) })
            sendNotification({
              assessmentId,
              body: comment.body,
              threadId: thread.id,
              commentId: thread.comments[0]?.id,
              trackingProps,
            })
          }}
        />
      )}
    </>
  )
}
